import {Plural, t, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {arrayToggle} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {DropdownItemTeammateAvatar} from '../../../../components/common/dropdowns/dropdownItemTeammateAvatar';
import {DeprecatedSearchDropdown} from '../../../../components/common/dropdowns/search/deprecatedSearchDropdown';
import {useSyncDropdownSource} from '../../../../components/common/dropdowns/source/hooks/useSyncDropdownSource';
import {buildTeammateDisplayName, doesTeammateMatchQuery} from '../../../../helpers/modelHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {PreferencesModel} from '../../../../models/api/preferencesModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {getFlatDelegationOwners} from '../../../../store/data/delegations/delegationsSelectors';

/*
 * Props.
 */

interface DelegationPreferenceControlProps {
  preferences: PreferencesModel;
  patchPreferences: HandlerOf<Partial<PreferencesModel>>;
}

/*
 * Component.
 */

export const DelegationPreferenceControl: FC<DelegationPreferenceControlProps> = (props) => {
  const delegationOwners = useAppSelector(getFlatDelegationOwners);
  const localize = useLocalize();
  const {preferences, patchPreferences} = props;

  const {hiddenDelegationOwnerIds} = preferences;
  const count = delegationOwners.filter((d) => !hiddenDelegationOwnerIds.includes(d.id)).length;
  const dropdownSource = useSyncDropdownSource<TeammateModel>(
    {
      items: delegationOwners,
      rowFilter: (items, query) =>
        _.chain(items)
          .compact()
          .filter((item) => doesTeammateMatchQuery(item, query))
          .value(),
      renderRow: (item, index) => {
        const isChecked = !hiddenDelegationOwnerIds.includes(item.id);
        return (
          <DropdownItem
            key={item.id}
            index={index}
            isChecked={isChecked}
            onSelect={() => {
              patchPreferences({
                hiddenDelegationOwnerIds: arrayToggle(hiddenDelegationOwnerIds, item.id),
              });
            }}
          >
            <DropdownItemTeammateAvatar teammate={item} />
            <DropdownItemContent isChecked={isChecked}>{buildTeammateDisplayName(item)}</DropdownItemContent>
          </DropdownItem>
        );
      },
    },
    [],
  );

  const renderDropdown = (popoverProps: PopoverTransferProps) => (
    <DeprecatedSearchDropdown
      {...popoverProps}
      source={dropdownSource}
      title={<Trans id="delegation-preference-dropdown-title">Teammates</Trans>}
      placeholder={localize(t('preferences-dropdown-search-placeholder')`Search`)}
    />
  );

  return (
    <DropdownBoxCoordinator
      value={<Plural value={count} one="1 teammate" other="# teammates" />}
      renderDropdown={renderDropdown}
      noInputMarginRight
    />
  );
};
